"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.watchOnlineStatus = exports.shallowEquals = exports.setSessionJSONItem = exports.requestIdleCallback = exports.noop = exports.isValidName = exports.isValidMachineSerial = exports.isStandardDate = exports.isServer = exports.isMatchingAddress = exports.isGermanDate = exports.getSessionJSONItem = exports.getLocaleByReference = exports.flatten = exports.escapeRegexChars = exports.convertISODateToGermanDate = exports.convertDateToISODate = exports.clearSessionJSONItem = exports.capitalize = exports.boldString = exports.REGEX_VALID_NAME = exports.REGEX_STANDARD_DATE_FORMAT = exports.REGEX_MACHINE_SERIAL_NUMBER = exports.REGEX_GERMAN_DATE_FORMAT = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
const _excluded = ["id", "addressId", "_type", "preferred", "creationDate", "lastModified"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */
const requestIdleCallback = fn => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(fn);
  } else {
    return setTimeout(() => fn(), 1);
  }
};
exports.requestIdleCallback = requestIdleCallback;
const watchOnlineStatus = (callback, win = window) => {
  const off = () => callback(false);
  const on = () => callback(true);
  win.addEventListener('offline', off);
  win.addEventListener('online', on);
  const unsubscribe = () => {
    win.removeEventListener('offline', off);
    win.removeEventListener('online', on);
  };
  return unsubscribe;
};

/**
 * Compares the primary fields of two address objects to determine if they match.
 * @param {Object} addr1
 * @param {Object} addr2
 * @returns {boolean}
 */
exports.watchOnlineStatus = watchOnlineStatus;
const isMatchingAddress = (addr1, addr2) => {
  const normalize = addr => {
    // eslint-disable-next-line no-unused-vars
    const {
        id,
        addressId,
        _type,
        preferred,
        creationDate,
        lastModified
      } = addr,
      normalized = (0, _objectWithoutProperties2.default)(addr, _excluded);
    return normalized;
  };
  return shallowEquals(normalize(addr1), normalize(addr2));
};

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
exports.isMatchingAddress = isMatchingAddress;
const shallowEquals = (a, b) => {
  for (let key in a) {
    if (!(key in b) || a[key] !== b[key]) {
      return false;
    }
  }
  for (let key in b) {
    if (!(key in a) || a[key] !== b[key]) {
      return false;
    }
  }
  return true;
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
exports.shallowEquals = shallowEquals;
const noop = () => {};

/**
 * Flattens a tree data structure into an array.
 * @param {*} node
 * @returns
 */
exports.noop = noop;
const flatten = (node, key = 'children') => {
  const children = (node[key] || []).reduce((a, b) => {
    return Array.isArray(b[key]) && !!b[key].length ? _objectSpread(_objectSpread({}, a), flatten(b, key)) : _objectSpread(_objectSpread({}, a), {}, {
      [b.id]: b
    });
  }, {});
  return _objectSpread({
    [node.id]: node
  }, children);
};

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
exports.flatten = flatten;
const isServer = typeof window === 'undefined';

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
exports.isServer = isServer;
const getSessionJSONItem = key => {
  if (isServer) {
    return undefined;
  }
  const item = window.sessionStorage.getItem(key);
  if (item) {
    return JSON.parse(item);
  } else {
    return undefined;
  }
};
/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
exports.getSessionJSONItem = getSessionJSONItem;
const setSessionJSONItem = (key, value) => {
  window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * clears an item in session storage
 * @param {string} key
 */
exports.setSessionJSONItem = setSessionJSONItem;
const clearSessionJSONItem = key => {
  window.sessionStorage.removeItem(key);
};

/**
 * escapes special regex characters
 * @param {string} str
 * @returns escaped string
 */
exports.clearSessionJSONItem = clearSessionJSONItem;
const escapeRegexChars = str => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

/**
 * bolds a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns stringified HTML Node
 */
exports.escapeRegexChars = escapeRegexChars;
const boldString = (str, substr) => {
  return str.replace(RegExp(escapeRegexChars(substr.trim()), 'gi'), '<b>$&</b>');
};

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns capitalized text
 */
exports.boldString = boldString;
const capitalize = text => {
  return text.toLowerCase().split(' ').map(s => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');
};

/**
 * Given a site and a locale reference, return the locale object
 * @param site - site to look for the locale
 * @param localeRef - the locale ref to look for in site supported locales
 * @return {object|undefined}
 */
exports.capitalize = capitalize;
const getLocaleByReference = (site, localeRef) => {
  if (!site) {
    throw new Error('Site is not defined. It is required to look for locale object');
  }
  return site.l10n.supportedLocales.find(locale => locale.id === localeRef || locale.alias === localeRef);
};
exports.getLocaleByReference = getLocaleByReference;
const REGEX_MACHINE_SERIAL_NUMBER = /^6000(1|5)0(1|2|3|4)([0-9A-Z]{11})$/;
exports.REGEX_MACHINE_SERIAL_NUMBER = REGEX_MACHINE_SERIAL_NUMBER;
const REGEX_STANDARD_DATE_FORMAT = /^(3[01]|[12][0-9]|0[1-9])\/(1[012]|0[1-9])\/((?:19|20)\d{2})$/;
exports.REGEX_STANDARD_DATE_FORMAT = REGEX_STANDARD_DATE_FORMAT;
const REGEX_GERMAN_DATE_FORMAT = /^(3[01]|[12][0-9]|0[1-9])\.(1[012]|0[1-9])\.((?:19|20)\d{2})$/;
exports.REGEX_GERMAN_DATE_FORMAT = REGEX_GERMAN_DATE_FORMAT;
const REGEX_VALID_NAME = /^[a-zA-Z' .\-\u00C0-\u00FF\u0100-\u017F\u0180-\u024F]+$/;
exports.REGEX_VALID_NAME = REGEX_VALID_NAME;
const isValidMachineSerial = serial => {
  return REGEX_MACHINE_SERIAL_NUMBER.test(serial);
};
exports.isValidMachineSerial = isValidMachineSerial;
const isStandardDate = date => {
  return REGEX_STANDARD_DATE_FORMAT.test(date);
};
exports.isStandardDate = isStandardDate;
const isGermanDate = date => {
  return REGEX_GERMAN_DATE_FORMAT.test(date);
};
exports.isGermanDate = isGermanDate;
const convertDateToISODate = val => {
  if (isGermanDate(val) || isStandardDate(val)) {
    return val.replace(/^(\d{2})(\.|\/)(\d{2})(\.|\/)(\d{4})/, '$3/$1/$5');
  }
  return val;
};
exports.convertDateToISODate = convertDateToISODate;
const convertISODateToGermanDate = val => {
  const date = new Date(val);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  return `${day > 9 ? day : '0' + day}.${month > 9 ? month : '0' + month}.${date.getFullYear()}`;
};
exports.convertISODateToGermanDate = convertISODateToGermanDate;
const isValidName = val => {
  return REGEX_VALID_NAME.test(val);
};
exports.isValidName = isValidName;